/**
 * /campaigns/:id — Campaign detail page (Story 7.5).
 *
 * Surface:
 *   - Header: name + active badge + kill-switch summary.
 *   - KB preview (first 500 chars of `kbText`).
 *   - "Editar" opens the same CampaignForm in `edit` mode.
 *   - Metrics placeholder (conversas ativas, conversão rate, opt-out rate)
 *     rendered as `—` until Story E9 ships the aggregate endpoint. The
 *     placeholder is NOT wired to any /admin/metrics call on purpose — this
 *     story does not commit to an API surface for metrics yet.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  BookOpenCheck,
  Pencil,
  Percent,
  Headphones,
  MessageSquareReply,
  PhoneCall,
} from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ApiError, apiClient } from '@/lib/api';
import {
  CampaignForm,
  DEFAULT_CAMPAIGN_FORM,
  type CampaignFormValue,
} from '@/components/campaigns/CampaignForm';
import type { ActiveHoursValue } from '@/components/campaigns/ActiveHoursInput';

interface CampaignDto {
  id: string;
  name: string;
  description: string | null;
  basicProductId: string | null;
  premiumProductId: string | null;
  triggerDelayHours: number;
  eligibilityWindowDays: number;
  activeHours: ActiveHoursValue;
  templateId: string | null;
  promptId: string | null;
  kbText: string | null;
  active: boolean;
  discountEnabled: boolean;
  audioEnabled: boolean;
  followupEnabled: boolean;
  callbackEnabled: boolean;
  openaiModel: string | null;
  openaiTemperature: number | null;
  openaiMaxTokens: number | null;
  createdAt: string;
  updatedAt: string;
}

function humanizeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as {
      error?: { code?: string; message?: string; field?: string; count?: number };
    };
    const code = body?.error?.code;
    if (code === 'VALIDATION_FAILED') {
      const field = body.error?.field ? ` (campo: ${body.error.field})` : '';
      return `${body.error?.message ?? 'Validação falhou.'}${field}`;
    }
    if (code === 'CAMPAIGN_NOT_FOUND') {
      return 'Campanha não encontrada.';
    }
    if (body?.error?.message) return body.error.message;
    return `${fallback} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

function toFormValue(c: CampaignDto): CampaignFormValue {
  return {
    name: c.name,
    description: c.description,
    // DTO may return null for either product — both are optional.
    basicProductId: c.basicProductId ?? '',
    premiumProductId: c.premiumProductId ?? '',
    triggerDelayHours: c.triggerDelayHours,
    eligibilityWindowDays: c.eligibilityWindowDays,
    activeHours: c.activeHours,
    templateId: c.templateId,
    promptId: c.promptId,
    kbText: c.kbText,
    active: c.active,
    killSwitches: {
      discountEnabled: c.discountEnabled,
      audioEnabled: c.audioEnabled,
      followupEnabled: c.followupEnabled,
      callbackEnabled: c.callbackEnabled,
    },
    openaiModel: c.openaiModel,
    openaiTemperature: c.openaiTemperature,
    openaiMaxTokens: c.openaiMaxTokens,
  };
}

function toApiBody(v: CampaignFormValue): Record<string, unknown> {
  return {
    name: v.name,
    description: v.description,
    // Empty form value means "unset" — send explicit null for either.
    basicProductId: v.basicProductId === '' ? null : v.basicProductId,
    premiumProductId: v.premiumProductId === '' ? null : v.premiumProductId,
    triggerDelayHours: v.triggerDelayHours,
    eligibilityWindowDays: v.eligibilityWindowDays,
    activeHours: v.activeHours,
    templateId: v.templateId,
    promptId: v.promptId,
    kbText: v.kbText,
    active: v.active,
    ...v.killSwitches,
    openaiModel: v.openaiModel,
    openaiTemperature: v.openaiTemperature,
    openaiMaxTokens: v.openaiMaxTokens,
  };
}

function SwitchIcon({
  on,
  label,
  children,
}: {
  on: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <span
      className={
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ' +
        (on
          ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
          : 'bg-muted text-muted-foreground')
      }
    >
      {children}
      {label}
      <span className="text-[10px] uppercase">{on ? 'ON' : 'OFF'}</span>
    </span>
  );
}

export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);

  const query = useQuery({
    queryKey: ['admin', 'campaigns', id],
    queryFn: () => apiClient.get<CampaignDto>(`/admin/campaigns/${id!}`),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (v: CampaignFormValue) =>
      apiClient.put<CampaignDto>(`/admin/campaigns/${id!}`, toApiBody(v)),
    onSuccess: () => {
      toast.success('Campanha atualizada.');
      setEditOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'campaigns', id] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'campaigns'] });
    },
    onError: (err) => {
      toast.error(humanizeError(err, 'Falha ao atualizar campanha.'));
    },
  });

  if (!id) return null;

  if (query.isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Carregando…</div>;
  }
  if (query.error || !query.data) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft aria-hidden /> Voltar
        </Button>
        <div className="p-6 text-sm text-destructive">
          {humanizeError(query.error, 'Falha ao carregar campanha.')}
        </div>
      </div>
    );
  }

  const c = query.data;
  const kbPreview = c.kbText ? c.kbText.slice(0, 500) : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <Button variant="ghost" size="sm" onClick={() => navigate('/campaigns')}>
            <ArrowLeft aria-hidden className="mr-1" /> Campanhas
          </Button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">{c.name}</h1>
            <span
              className={
                'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ' +
                (c.active
                  ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                  : 'bg-muted text-muted-foreground')
              }
            >
              {c.active ? 'Ativa' : 'Inativa'}
            </span>
          </div>
          {c.description && <p className="text-sm text-muted-foreground">{c.description}</p>}
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <Button variant="outline" asChild>
            <Link to={`/campaigns/${c.id}/followup`}>
              <BookOpenCheck aria-hidden className="mr-1" /> Gerenciar followups
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to={`/campaigns/${c.id}/variants`}>
              <BookOpenCheck aria-hidden className="mr-1" /> Variantes
            </Link>
          </Button>
          <Button onClick={() => setEditOpen(true)}>
            <Pencil aria-hidden className="mr-1" /> Editar
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Kill switches</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <SwitchIcon on={c.discountEnabled} label="Desconto">
            <Percent size={12} />
          </SwitchIcon>
          <SwitchIcon on={c.audioEnabled} label="Áudio">
            <Headphones size={12} />
          </SwitchIcon>
          <SwitchIcon on={c.followupEnabled} label="Follow-up">
            <MessageSquareReply size={12} />
          </SwitchIcon>
          <SwitchIcon on={c.callbackEnabled} label="Callback">
            <PhoneCall size={12} />
          </SwitchIcon>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Timing</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p>
              <span className="text-muted-foreground">Trigger delay:</span> {c.triggerDelayHours}h
            </p>
            <p>
              <span className="text-muted-foreground">Janela de elegibilidade:</span>{' '}
              {c.eligibilityWindowDays} dias
            </p>
            <p>
              <span className="text-muted-foreground">Horário ativo:</span> {c.activeHours.start}–
              {c.activeHours.end} ({c.activeHours.timezone})
            </p>
            <p>
              <span className="text-muted-foreground">Dias:</span>{' '}
              {c.activeHours.weekdays.join(', ')}
            </p>
          </CardContent>
        </Card>

        {/*
         * Metrics aggregate placeholder — see Story 7.12c (backlog) for the
         * real per-campaign metrics endpoint. Story 7.12 Dashboard already
         * provides global metrics via `/admin/metrics/dashboard`; per-campaign
         * aggregation is its scoped follow-up.
         */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Métricas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p>
              <span className="text-muted-foreground">Conversas ativas:</span> —
            </p>
            <p>
              <span className="text-muted-foreground">Conversão:</span> —
            </p>
            <p>
              <span className="text-muted-foreground">Opt-out rate:</span> —
            </p>
            <p className="text-xs italic text-muted-foreground">
              Agregados virão com Epic 9 (dashboard de analytics).
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Knowledge Base (preview)</CardTitle>
        </CardHeader>
        <CardContent>
          {kbPreview ? (
            <pre className="max-h-60 overflow-auto whitespace-pre-wrap rounded bg-muted p-3 text-xs">
              {kbPreview}
              {c.kbText && c.kbText.length > 500 ? '\n…' : ''}
            </pre>
          ) : (
            <p className="text-sm text-muted-foreground">Nenhum conteúdo de KB configurado.</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] flex-col gap-4 overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Editar campanha</DialogTitle>
            <DialogDescription>
              Campos vazios mantêm o valor atual. Kill switches desligados não são reativados
              automaticamente (AC4).
            </DialogDescription>
          </DialogHeader>
          <CampaignForm
            mode="edit"
            initial={toFormValue(c) ?? DEFAULT_CAMPAIGN_FORM}
            submitting={updateMutation.isPending}
            onCancel={() => setEditOpen(false)}
            onSubmit={(next) => updateMutation.mutate(next)}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CampaignDetailPage;
